import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { FormControlLabel, Switch, Select, MenuItem, Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getEffectiveValue } from '../../utils/receiptsUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const SpendingOverTime = ({ receipts }) => {
  const [byCategory, setByCategory] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [lookbackMonths, setLookbackMonths] = useState(3);
  const [errorToast, setErrorToast] = useState({ open: false, message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    prepareChartData();
  }, [receipts, byCategory, lookbackMonths]);

  const prepareChartData = async () => {
    try {
      console.log('Preparing chart data. Receipts:', receipts);

      if (!receipts || receipts.length === 0) {
        console.log('No receipts data available');
        setChartData(null);
        return;
      }

      const monthlyData = {};
      const categories = new Set();
      let invalidDateCount = 0;

      // Calculate the start date for the lookback period
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - lookbackMonths);

      // First, filter receipts and prepare the base monthly data
      const validReceipts = receipts.filter(receipt => {
        if (!getEffectiveValue(receipt, 'date') || !getEffectiveValue(receipt, 'total_cost')) {
          console.warn('Receipt missing date or total_cost:', receipt);
          return false; // Skip this receipt
        }

        let receiptDate;
        try {
          receiptDate = new Date(getEffectiveValue(receipt, 'date'));
          if (isNaN(receiptDate.getTime())) {
            throw new Error('Invalid date');
          }
        } catch (error) {
          console.error(`Error parsing date for receipt: ${receipt.uuid}`, error);
          invalidDateCount++;
          return false;
        }
        if (receiptDate < startDate) return false; // Skip receipts outside the lookback period

        // Format the month key to ensure proper chronological sorting
        const monthYear = receiptDate.toISOString().substring(0, 7); // YYYY-MM format
        
        if (!monthlyData[monthYear]) {
          monthlyData[monthYear] = { total: 0, categories: {} };
        }

        monthlyData[monthYear].total += parseFloat(getEffectiveValue(receipt, 'total_cost'));
        return true;
      });

      // Fetch all receipt items in parallel
      const itemsPromises = validReceipts.map(receipt => 
        axios.get(`${API_BASE_URL}/get_receipt_items`, {
          params: { receipt_id: receipt.uuid },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        })
        .then(response => ({
          items: response.data,
          date: new Date(receipt.date)
        }))
        .catch(error => {
          console.error('Error fetching receipt items:', error);
          return { items: [], date: new Date(receipt.date) };
        })
      );

      const results = await Promise.all(itemsPromises);

      // Add null check and filtering for results
      if (!results) {
        console.warn('No results from receipt items fetch');
        return;
      }

      // Filter out any null or invalid results
      const validResults = results.filter(result => result && result.items && result.date);

      // Process all valid items
      validResults.forEach(({ items, date }) => {
        const monthYear = date.toISOString().substring(0, 7); // YYYY-MM format

        items.forEach(item => {
          const category = getEffectiveValue(item, 'high_level_category') || 'Uncategorized';
          const price = parseFloat(getEffectiveValue(item, 'item_price')) || 0;

          if (!category || price === 0) {
            console.warn('Item missing high_level_category or item_price:', item);
            return;
          }

          categories.add(category);

          if (!monthlyData[monthYear].categories[category]) {
            monthlyData[monthYear].categories[category] = 0;
          }
          monthlyData[monthYear].categories[category] += price;
        });
      });

      if (invalidDateCount > 0) {
        setErrorToast({
          open: true,
          message: `${invalidDateCount} receipt${invalidDateCount > 1 ? 's' : ''} had invalid date${invalidDateCount > 1 ? 's' : ''} and ${invalidDateCount > 1 ? 'were' : 'was'} skipped during parsing.`
        });
      }

      const sortedMonths = Object.keys(monthlyData).sort();

      // Format month labels for display
      const formatMonthLabel = (monthYear) => {
        const [year, month] = monthYear.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };

      const datasets = byCategory
        ? Array.from(categories).map(category => ({
            label: category,
            data: sortedMonths.map(month => monthlyData[month].categories[category] || 0),
            borderColor: getRandomColor(),
            fill: false,
          }))
        : [{
            label: 'Total Spending',
            data: sortedMonths.map(month => monthlyData[month].total),
            borderColor: 'rgb(75, 192, 192)',
            fill: false,
          }];

      setChartData({
        labels: sortedMonths.map(formatMonthLabel),
        datasets: datasets,
      });
    } catch (error) {
      console.error('Error preparing chart data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRandomColor = () => {
    return `hsl(${Math.random() * 360}, 70%, 50%)`;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'white',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: byCategory ? 'Spending by Category Over Time' : 'Total Spending Over Time',
        color: 'white',
        font: {
          size: 16
        }
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
          color: 'white',
        },
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Amount ($)',
          color: 'white',
        },
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        beginAtZero: true,
      },
    },
  };

  const downloadCSV = () => {
    if (!chartData) return;

    let csvContent = byCategory
      ? "Month,Category,Amount\n"
      : "Month,Amount\n";

    chartData.labels.forEach((month, index) => {
      if (byCategory) {
        chartData.datasets.forEach(dataset => {
          csvContent += `${month},${dataset.label},${dataset.data[index]}\n`;
        });
      } else {
        csvContent += `${month},${chartData.datasets[0].data[index]}\n`;
      }
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `spending_over_time_${lookbackMonths}_months.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseErrorToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorToast({ ...errorToast, open: false });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4 flex flex-col space-y-2">
        <div className="flex justify-between items-center">
          <Select
            value={lookbackMonths}
            onChange={(e) => setLookbackMonths(e.target.value)}
            style={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              height: '32px',
              fontSize: '0.875rem',
              flexGrow: 1,
              marginRight: '8px',
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: '#1F2937',
                  color: 'white',
                },
              },
            }}
          >
            <MenuItem value={3}>Last 3 months</MenuItem>
            <MenuItem value={6}>Last 6 months</MenuItem>
            <MenuItem value={9}>Last 9 months</MenuItem>
            <MenuItem value={12}>Last 12 months</MenuItem>
            <MenuItem value={14}>Last 14 months</MenuItem>
          </Select>
          <Button
            onClick={downloadCSV}
            variant="outlined"
            startIcon={<DownloadIcon />}
            style={{
              color: 'white',
              borderColor: 'white',
              height: '32px',
              fontSize: '0.875rem',
            }}
          >
            CSV
          </Button>
        </div>
        <div className="flex justify-center">
          <FormControlLabel
            control={
              <Switch 
                checked={byCategory} 
                onChange={() => setByCategory(!byCategory)}
                color="primary"
              />
            }
            label="Show by Category"
            style={{ color: 'white', margin: 0 }}
          />
        </div>
      </div>
      <div className="flex-grow" style={{ maxHeight: '500px', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <CircularProgress />
          </div>
        ) : chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <p className="text-center text-white">No data available</p>
        )}
      </div>
      <Snackbar open={errorToast.open} autoHideDuration={6000} onClose={handleCloseErrorToast}>
        <Alert onClose={handleCloseErrorToast} severity="error" sx={{ width: '100%' }}>
          {errorToast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SpendingOverTime;
